import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/apiService';

const TOKEN_KEY = 'rmw_token';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setIsLoading(false);
      return;
    }
    authAPI.me()
      .then((user) => setCurrentUser(user))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setIsLoading(false));
  }, []);

  const register = useCallback(async (username, password, displayName = '') => {
    try {
      const data = await authAPI.register(username, password, displayName);
      localStorage.setItem(TOKEN_KEY, data.token);
      setCurrentUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const data = await authAPI.login(username, password);
      localStorage.setItem(TOKEN_KEY, data.token);
      setCurrentUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setCurrentUser(null);
  }, []);

  const updateProfile = useCallback((updates) => {
    if (!currentUser) return { success: false, error: 'Not logged in' };
    const updated = { ...currentUser, ...updates };
    setCurrentUser(updated);
    return { success: true, user: updated };
  }, [currentUser]);

  const devSetRole = useCallback((role) => {
    if (process.env.NODE_ENV === 'production') return;
    const base = currentUser || {
      id: 'dev-mock-user',
      username: 'dev_tester',
      displayName: 'Dev Tester',
      subscriptionTier: 'advanced',
    };
    setCurrentUser({ ...base, role });
  }, [currentUser]);

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    isLoading,
    register,
    login,
    logout,
    updateProfile,
    ...(process.env.NODE_ENV !== 'production' && { devSetRole }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
